import { useCallback, useMemo } from "react"
import { Avatar, Divider, IconButton, Stack, Typography } from "@mui/material"
import { CloseIcon, FilterIcon } from "shared/icons"
import {
	PushPin as PinIcon,
	PushPinOutlined as UnPinIcon
} from '@mui/icons-material'
import { GridRowId } from "@mui/x-data-grid"

import { GridColumnDefMap, GridFilter } from "../../types"

import { CandidatesFilter } from "./candidates"
import { TextFilter } from "./text"
import { SentenseFilter } from "./sentense"
import { HistgramFilter } from "./histgram"
import { RangeFilter } from "./range"
import { AccessTimeFilter } from "./accessTime"
import { DateFilter } from "./date"

type GridFilterInspectorProps<T> = {
	field: string | null
	rowId: GridRowId | null
	columnsDef: GridColumnDefMap<T>
	dataList: T[]
	filters: GridFilter[]
	setFilters: (filters: GridFilter[]) => void
	onClose: () => void
	isKeeped: boolean
	keepFilterColumns: string[]
	setKeepFilterColumns: (fields: string[]) => void
}
export const GridFilterInspector = <T,>({
	field,
	rowId,
	columnsDef,
	dataList,
	filters,
	setFilters,
	onClose,
	isKeeped, keepFilterColumns, setKeepFilterColumns
}: GridFilterInspectorProps<T>) => {
	const current = useMemo(
		() => {
			if (!field) return null
			let fieldDef = columnsDef[field]
			if (fieldDef && fieldDef.filterField) fieldDef = columnsDef[fieldDef.filterField]
			if (!fieldDef) return null
			const filter = filters.find(f => f.field === field)
			const row = rowId !== null ? dataList[rowId as number] : null
			const value = row ? fieldDef.value(row) : null
			const values = ((value && Array.isArray(value)) ? value : [value])
				.filter(value => typeof value === 'string' ? value.trim() !== '' : true)
			if (fieldDef.headerName && field.startsWith('project-')) {
				const [projectNo, title] = fieldDef.headerName.split(':')
				return { fieldDef, filter, values, projectNo, title }
			}
			return { fieldDef, filter, values, title: fieldDef.headerName ?? '' }
		},
		[field, columnsDef, filters, rowId, dataList]
	)
	const onClickPin = useCallback(
		() => {
			if (!field) return
			if (isKeeped) {
				setKeepFilterColumns(keepFilterColumns.filter(f => f !== field).slice())
			} else {
				setKeepFilterColumns(keepFilterColumns.concat([field]))
			}
		},
		[field, isKeeped, keepFilterColumns, setKeepFilterColumns]
	)
	return (current && field) ? (
		<Stack width='100%' height='100%' boxSizing="border-box">
			<Stack direction="row" alignItems="center" paddingY={0.5} paddingX={1}>
				<FilterIcon fontSize="small" sx={{ marginRight: 0.5 }}/>
				{current.projectNo && (
					<Avatar sx={{
						width: "1rem", height: "1rem", fontSize: "0.75rem", marginRight: 0.5,
						color: "black", bgcolor: "#f5f5f5", border: '1px solid #dadde9',
						borderRadius: 1
					}}>
						{current.projectNo}
					</Avatar>
				)}
				<Typography component="div" flexGrow={1} variant="body2">{current.title}</Typography>
				<IconButton size="small" onClick={onClickPin} color={isKeeped ? "primary" : "default"}>
					{isKeeped ? <PinIcon fontSize="small"/> : <UnPinIcon fontSize="small"/>}
				</IconButton>
				<IconButton size="small" onClick={onClose}>
					<CloseIcon fontSize="small"/>
				</IconButton>
			</Stack>
			<Divider/>

			{current.fieldDef.filterType === 'candidates' ? (
				<CandidatesFilter
					field={field}
					fieldDef={current.fieldDef}
					columnsDef={columnsDef}
					dataList={dataList}
					values={current.values}
					filters={filters}
					filter={current.filter}
					setFilters={setFilters}
				/>
			) : current.fieldDef.filterType === 'text' ? (
				<TextFilter
					field={field}
					values={current.values}
					filters={filters}
					filter={current.filter}
					setFilters={setFilters}
				/>
			) : current.fieldDef.filterType === 'sentense' ? (
				<SentenseFilter
					field={field}
					values={current.values}
					filters={filters}
					filter={current.filter}
					setFilters={setFilters}
				/>
			) : current.fieldDef.filterType === 'histgram' ? (
				<HistgramFilter
					field={field}
					fieldDef={current.fieldDef}
					columnsDef={columnsDef}
					dataList={dataList}
					values={current.values}
					filters={filters}
					filter={current.filter}
					setFilters={setFilters}
				/>
			) : current.fieldDef.filterType === 'range' ? (
				<RangeFilter
					field={field}
					fieldDef={current.fieldDef}
					filters={filters}
					filter={current.filter}
					setFilters={setFilters}
				/>
			) : current.fieldDef.filterType === 'accessTime' ? (
				<AccessTimeFilter
					field={field}
					fieldDef={current.fieldDef}
					columnsDef={columnsDef}
					dataList={dataList}
					values={current.values}
					filters={filters}
					filter={current.filter}
					setFilters={setFilters}
				/>
			) : current.fieldDef.filterType === 'date' ? (
				<DateFilter
					field={field}
					fieldDef={current.fieldDef}
					columnsDef={columnsDef}
					dataList={dataList}
					values={current.values}
					filters={filters}
					filter={current.filter}
					setFilters={setFilters}
				/>
			) : <></>}
		</Stack>
	) : <></>
}

